/**
 * Jeopardy's config: deliberately empty. Everything a host decides is content (clues,
 * values, timers, the round wager pool, edited live) or a live action. The live board
 * is `JeopardyState.board`, since it changes on almost every action.
 */
import { z } from 'zod';
/**
 * How auto-gradable clues are played:
 *  - `highScore` — everyone answers at once, auto-graded (the collect flow).
 *  - `buzzer` — race to buzz; the first in answers alone and is auto-graded, a wrong answer
 *    locks them out and re-opens buzzing for the rest (Play Now, hostless).
 *
 * Only this field steers the reducer. The other Play Now toggles (lose points on a wrong
 * answer, speed bonus) are realized as per-clue fields (`negativePoints`, `speedBonus`) when the
 * board is generated, so they need no runtime config and no second source of truth.
 *
 * A hosted show never sets this; the default keeps the classic verbal-buzzer board unchanged,
 * since `answerMode` only governs the auto-gradable types, and verbal always uses buzzers.
 */
export const answerModeSchema = z.enum(['highScore', 'buzzer']);
/**
 * `.strict()` so a stray key fails at the boundary instead of being stored and never read.
 * `answerMode` has a default, so stored `{}` configs (every hosted session) stay valid.
 *
 * Both fields are `internal`: Play Now sets them programmatically, so they are kept out of the
 * host setup form (`configFormOf`), which keeps Jeopardy's form empty — everything a host decides
 * is still content or a live action.
 */
export const jeopardyConfigSchema = z.strictObject({
    answerMode: answerModeSchema.default('highScore').meta({ internal: true }),
    /**
     * A self-driving game with no host: players take turns picking tiles (a pick request to the
     * current picker), clues reveal and grade themselves, and the board advances on a timer. False
     * for every hosted show, which keeps the host in control of picking, revealing and advancing.
     */
    hostless: z.boolean().default(false).meta({ internal: true }),
    /**
     * Hostless only: nobody picks. Clues reveal one after another in layout order and the segment
     * finishes after the last (Play Now's Quick Game).
     */
    sequential: z.boolean().default(false).meta({ internal: true }),
});
/**
 * How long a Jeopardy segment takes, for the show builder's estimate. Measured, not
 * derived: clue count is a poor predictor, since the time goes into the arguing.
 */
export const JEOPARDY_ESTIMATED_MINUTES = 45;
//# sourceMappingURL=config.js.map